/**
 * Actor Mailbox Viewer - Message Queue Visualization
 *
 * FERP CRITICAL: Real-time actor message queue monitoring
 *
 * Backend: ActorRepository (DynamoDB-backed)
 * ActorRuntime: Message-passing concurrency model
 *
 * Features:
 * - Real-time mailbox updates
 * - Message details display
 * - Priority-based sorting
 * - Message status tracking
 * - Payload inspection
 * - Message timeline visualization
 *
 * Revenue Impact: Part of $300K/year Actor Runtime Dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Stack,
  Paper,
  Table,
  Badge,
  Group,
  Text,
  Button,
  Modal,
  JsonInput,
  Timeline,
  Select,
  Alert,
  LoadingOverlay,
  ActionIcon,
  Tooltip,
  Code,
  Divider,
} from '@mantine/core';
import {
  IconMessage,
  IconClock,
  IconAlertTriangle,
  IconCheck,
  IconRefresh,
  IconEye,
  IconArrowUp,
  IconArrowDown,
  IconMinus,
} from '@tabler/icons-react';
import { useActorRuntime, ActorMessage } from '../hooks/useActorRuntime';

interface ActorMailboxViewerProps {
  actorId: string;
}

/**
 * Actor Mailbox Viewer Component
 */
export function ActorMailboxViewer({ actorId }: ActorMailboxViewerProps) {
  const { mailbox, loading, error, fetchMailbox } = useActorRuntime();

  const [filterStatus, setFilterStatus] = useState<string>('');
  const [sortBy, setSortBy] = useState<'timestamp' | 'priority'>('timestamp');
  const [selectedMessage, setSelectedMessage] = useState<ActorMessage | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Fetch mailbox on mount
  useEffect(() => {
    if (actorId) {
      fetchMailbox(actorId, 100);
    }
  }, [actorId, fetchMailbox]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (actorId) {
      const interval = setInterval(() => {
        fetchMailbox(actorId, 100);
      }, 5000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [actorId, fetchMailbox]);

  // Filter and sort messages
  const filteredMessages = mailbox
    .filter((msg) => {
      if (filterStatus && msg.status !== filterStatus) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'timestamp') {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      } else {
        return b.priority - a.priority;
      }
    });

  // Message status color helper
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'blue';
      case 'processing':
        return 'yellow';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Priority icon helper
  const getPriorityIcon = (priority: number) => {
    if (priority >= 8) {
      return <IconArrowUp size={16} color="red" />;
    } else if (priority >= 5) {
      return <IconMinus size={16} color="orange" />;
    } else {
      return <IconArrowDown size={16} color="gray" />;
    }
  };

  // Priority label helper
  const getPriorityLabel = (priority: number): string => {
    if (priority >= 8) return 'High';
    if (priority >= 5) return 'Medium';
    return 'Low';
  };

  // Message stats
  const stats = {
    total: mailbox.length,
    pending: mailbox.filter((m) => m.status === 'pending').length,
    processing: mailbox.filter((m) => m.status === 'processing').length,
    completed: mailbox.filter((m) => m.status === 'completed').length,
    failed: mailbox.filter((m) => m.status === 'failed').length,
  };

  return (
    <Stack gap="md">
      <LoadingOverlay visible={loading} />

      {/* Error Alert */}
      {error && (
        <Alert color="red" title="Error" icon={<IconAlertTriangle size={16} />}>
          {error.message}
        </Alert>
      )}

      {/* Mailbox Stats */}
      <Paper shadow="xs" p="md">
        <Group justify="space-between">
          <Group gap="lg">
            <div>
              <Text size="xs" color="dimmed">
                Total Messages
              </Text>
              <Text size="lg" fw={700}>
                {stats.total}
              </Text>
            </div>
            <Divider orientation={'vertical' as const} />
            <Group gap="md">
              <Badge color="blue" size="lg">
                {stats.pending} Pending
              </Badge>
              <Badge color="yellow" size="lg">
                {stats.processing} Processing
              </Badge>
              <Badge color="green" size="lg">
                {stats.completed} Completed
              </Badge>
              {stats.failed > 0 && (
                <Badge color="red" size="lg">
                  {stats.failed} Failed
                </Badge>
              )}
            </Group>
          </Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => fetchMailbox(actorId, 100)}
          >
            Refresh
          </Button>
        </Group>
      </Paper>

      {/* Filters and Sorting */}
      <Paper shadow="xs" p="md">
        <Group>
          <Select
            label="Filter by Status"
            placeholder="All statuses"
            clearable
            data={[
              { value: 'pending', label: 'Pending' },
              { value: 'processing', label: 'Processing' },
              { value: 'completed', label: 'Completed' },
              { value: 'failed', label: 'Failed' },
            ]}
            value={filterStatus}
            onChange={(value) => setFilterStatus(value || '')}
            style={{ width: 200 }}
          />
          <Select
            label="Sort by"
            data={[
              { value: 'timestamp', label: 'Timestamp (Newest First)' },
              { value: 'priority', label: 'Priority (Highest First)' },
            ]}
            value={sortBy}
            onChange={(value) => setSortBy((value as 'timestamp' | 'priority') || 'timestamp')}
            style={{ width: 250 }}
          />
        </Group>
      </Paper>

      {/* Message List */}
      <Paper shadow="xs" p="md">
        <Table highlightOnHover>
          <thead>
            <tr>
              <th>Message ID</th>
              <th>From</th>
              <th>Type</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Timestamp</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMessages.map((message) => (
              <tr key={message.messageId}>
                <td>
                  <Code>{message.messageId.substring(0, 8)}...</Code>
                </td>
                <td>
                  <Text size="sm">{message.from}</Text>
                </td>
                <td>
                  <Badge variant="outline">{message.messageType}</Badge>
                </td>
                <td>
                  <Group gap="xs">
                    {getPriorityIcon(message.priority)}
                    <Text size="sm">{getPriorityLabel(message.priority)}</Text>
                    <Text size="xs" color="dimmed">
                      ({message.priority})
                    </Text>
                  </Group>
                </td>
                <td>
                  <Badge color={getStatusColor(message.status)}>{message.status}</Badge>
                </td>
                <td>
                  <Group gap="xs">
                    <IconClock size={14} color="gray" />
                    <Text size="sm">{new Date(message.timestamp).toLocaleString()}</Text>
                  </Group>
                </td>
                <td>
                  <Tooltip label="View Details">
                    <ActionIcon
                      color="blue"
                      variant="light"
                      onClick={() => {
                        setSelectedMessage(message);
                        setDetailsModalOpen(true);
                      }}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {filteredMessages.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {filterStatus ? 'No messages match the selected filter' : 'Mailbox is empty'}
          </Text>
        )}
      </Paper>

      {/* Message Timeline (for recent messages) */}
      {filteredMessages.length > 0 && (
        <Paper shadow="xs" p="md">
          <Text size="sm" fw={500} mb="md">
            Recent Message Timeline
          </Text>
          <Timeline active={-1} lineWidth={2}>
            {filteredMessages.slice(0, 10).map((message) => (
              <Timeline.Item
                key={message.messageId}
                bullet={<IconMessage size={12} />}
                title={
                  <Group gap="xs">
                    <Badge color={getStatusColor(message.status)} size="sm">
                      {message.status}
                    </Badge>
                    <Text size="sm" fw={500}>
                      {message.messageType}
                    </Text>
                  </Group>
                }
              >
                <Text size="xs" color="dimmed">
                  From: {message.from}
                </Text>
                <Text size="xs" color="dimmed">
                  {new Date(message.timestamp).toLocaleString()}
                </Text>
                {message.status === 'failed' && (
                  <Alert color="red" mt="xs" py="xs" px="sm">
                    <Text size="xs">Message processing failed</Text>
                  </Alert>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        </Paper>
      )}

      {/* Message Details Modal */}
      <Modal
        opened={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        title="Message Details"
        size="lg"
      >
        {selectedMessage && (
          <Stack gap="md">
            {/* Message Metadata */}
            <Paper withBorder p="md">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Message ID
                  </Text>
                  <Code>{selectedMessage.messageId}</Code>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    From
                  </Text>
                  <Text size="sm">{selectedMessage.from}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    To
                  </Text>
                  <Text size="sm">{selectedMessage.to}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Message Type
                  </Text>
                  <Badge variant="outline">{selectedMessage.messageType}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Priority
                  </Text>
                  <Group gap="xs">
                    {getPriorityIcon(selectedMessage.priority)}
                    <Text size="sm">
                      {getPriorityLabel(selectedMessage.priority)} ({selectedMessage.priority})
                    </Text>
                  </Group>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Status
                  </Text>
                  <Badge color={getStatusColor(selectedMessage.status)}>
                    {selectedMessage.status}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Timestamp
                  </Text>
                  <Text size="sm">{new Date(selectedMessage.timestamp).toLocaleString()}</Text>
                </Group>
              </Stack>
            </Paper>

            {/* Message Payload */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                Message Payload
              </Text>
              <JsonInput
                value={JSON.stringify(selectedMessage.payload, null, 2)}
                readOnly
                minRows={10}
                maxRows={20}
                formatOnBlur
                autosize
              />
            </div>

            {/* Status-Specific Information */}
            {selectedMessage.status === 'failed' && (
              <Alert color="red" title="Processing Failed" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">
                  This message failed to process. Check the payload for errors or retry the
                  operation.
                </Text>
              </Alert>
            )}

            {selectedMessage.status === 'completed' && (
              <Alert color="green" title="Processing Complete" icon={<IconCheck size={16} />}>
                <Text size="sm">This message has been successfully processed.</Text>
              </Alert>
            )}

            {selectedMessage.status === 'processing' && (
              <Alert color="yellow" title="Processing In Progress" icon={<IconClock size={16} />}>
                <Text size="sm">This message is currently being processed by the actor.</Text>
              </Alert>
            )}

            {selectedMessage.status === 'pending' && (
              <Alert color="blue" title="Pending Processing" icon={<IconMessage size={16} />}>
                <Text size="sm">This message is waiting in the queue to be processed.</Text>
              </Alert>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
